import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { dollars, fetchPrice, type Price } from "../lib/license.ts";
import { Link } from "../lib/router.tsx";

// The home page's pitch for the paid report, shown above the free calculators: three marks, then the price and the
// button together on one card. On wide screens a sample sheet sits on the right; phones skip it to keep the section
// short. The sheet is a sample
// of the finished report: the lines a futures and prop trader types into their filing software. Its figures are a
// labeled example (the walkthrough's $5,000 of payouts and $1,800 of costs), not anyone's numbers.
const SAMPLE: { form: string; what: string; lines: { line: string; label: string; amount: string; total?: boolean }[] }[] = [
  {
    form: "Form 6781",
    what: "Futures in your own account",
    lines: [
      { line: "Line 8", label: "Short-term, 40%", amount: "$4,800" },
      { line: "Line 9", label: "Long-term, 60%", amount: "$7,200" },
    ],
  },
  {
    form: "Schedule C",
    what: "Prop firm payouts",
    lines: [
      { line: "Line 1", label: "Gross receipts", amount: "$5,000" },
      { line: "Line 28", label: "Total expenses", amount: "$1,800" },
      { line: "Line 31", label: "Net profit", amount: "$3,200", total: true },
    ],
  },
];

// The three things the report does that a spreadsheet won't, each with a small drawn mark.
const MARKS: { name: string; line: string; icon: ReactNode }[] = [
  {
    name: "Counted once",
    line: "However many statements show a payout",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <rect x="4" y="3" width="16" height="18" rx="2" />
        <path d="M8 12l3 3 5-6" />
      </svg>
    ),
  },
  {
    name: "Shows its steps",
    line: "Tap any figure to see why",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M5 6h14M5 12h10M5 18h6" />
        <circle cx="19" cy="17" r="3" />
      </svg>
    ),
  },
  {
    name: "PDF and versions",
    line: "Compare when a corrected 1099 comes",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 3h7l5 5v13H7z" />
        <path d="M14 3v5h5M10 14h6M10 17h4" />
      </svg>
    ),
  },
];

function shortDate(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function ReportPitch({ href, taxYear }: { href: string; taxYear: number }) {
  // undefined while loading; null when buying isn't open or the server can't be reached.
  const [price, setPrice] = useState<Price | null | undefined>(undefined);
  useEffect(() => {
    let live = true;
    void fetchPrice().then((p) => live && setPrice(p?.available ? p : null));
    return () => {
      live = false;
    };
  }, []);

  return (
    <section className="band pitch" aria-labelledby="pitch-title">
      <div className="shell pitch-inner">
        <div className="pitch-copy" data-reveal>
          <p className="pitch-kicker">The {taxYear} tax report</p>
          <h2 id="pitch-title">Your whole trading year, ready to file.</h2>
          <ul className="pitch-marks">
            {MARKS.map((m) => (
              <li key={m.name}>
                <span className="pitch-mark" aria-hidden="true">
                  {m.icon}
                </span>
                <span>
                  <strong>{m.name}</strong>
                  <small>{m.line}</small>
                </span>
              </li>
            ))}
          </ul>
          <div className="pitch-pay">
            <span className="pitch-pay-tab" aria-hidden="true">
              Tax report
            </span>
            {price && (
              <div className="pitch-pay-price">
                <p className="pitch-pay-amount">
                  {dollars(price.amountCents)}
                  {price.earlyBird && <s aria-label={`regular price ${dollars(price.regularCents)}`}>{dollars(price.regularCents)}</s>}
                </p>
                <p className="pitch-pay-when">
                  {price.earlyBird
                    ? `Early bird through ${shortDate(price.earlyBirdEnds)}, then ${dollars(price.regularCents)}.`
                    : `One payment for the ${taxYear} tax year.`}
                </p>
              </div>
            )}
            <Link href={href} className="btn btn--primary">
              Start my {taxYear} report
            </Link>
          </div>
          <p className="pitch-fine">Free to fill in. Pay once to unlock.</p>
        </div>

        <figure className="pitch-sheet" data-reveal style={{ "--i": 1 } as CSSProperties}>
          <figcaption className="pitch-sheet-head">
            <span>{taxYear} tax report</span>
            <span className="pitch-sample">Example</span>
          </figcaption>
          {SAMPLE.map((f) => (
            <div key={f.form} className="pitch-form">
              <p className="pitch-form-name">
                {f.form} <span>{f.what}</span>
              </p>
              <dl>
                {f.lines.map((l) => (
                  <div key={l.line} className={l.total ? "is-total" : undefined}>
                    <dt>
                      <span className="pitch-line">{l.line}</span> {l.label}
                    </dt>
                    <dd>{l.amount}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
          <p className="pitch-why">Why this amount? Every figure opens to show its steps.</p>
        </figure>
      </div>
    </section>
  );
}
